import type { PackagingItemHeavyMetalsAuditStatus } from '@/shared/hooks/api/packagingItemsOverview'
import type { PackagingHeavyMetalsAuditVerdict } from '@/shared/hooks/api/packagingSuppliers'
import { heavyMetalsAuditVerdictToOverviewStatus } from './packagingItemHeavyMetalsAuditOverviewClient'

export const PACKAGING_ITEMS_OVERVIEW_HEAVY_METALS_AUDIT_STATUS_FILTER_VALUES =
  [
    'not_audited',
    'passed',
    'failed',
    'flagged',
  ] as const satisfies readonly PackagingItemHeavyMetalsAuditStatus[]

export const PACKAGING_ITEMS_OVERVIEW_HEAVY_METALS_AUDIT_VERDICT_FILTER_VALUES =
  [
    'TEST_REPORT_REDUCED_SUFFICIENTLY',
    'TEST_REPORT_PRESENT_NOT_REDUCED_SUFFICIENTLY',
    'TEST_REPORT_PRESENT_MATERIAL_MISMATCH',
    'TEST_REPORT_PRESENT_DEFICIENT',
    'STATEMENT',
    'STATEMENT_PRESENT_NOT_RELEVANT',
    'STATEMENT_IS_TEST_REPORT_REDUCED_SUFFICIENTLY',
    'STATEMENT_IS_TEST_REPORT_NOT_REDUCED_SUFFICIENTLY',
    'STATEMENT_IS_TEST_REPORT_MATERIAL_MISMATCH',
    'STATEMENT_IS_TEST_REPORT_NOT_RELEVANT',
    'TEST_REPORT_NOT_PRESENT',
  ] as const satisfies readonly PackagingHeavyMetalsAuditVerdict[]

export const PACKAGING_ITEMS_OVERVIEW_HEAVY_METALS_AUDIT_FILTER_VALUES = [
  ...PACKAGING_ITEMS_OVERVIEW_HEAVY_METALS_AUDIT_STATUS_FILTER_VALUES,
  ...PACKAGING_ITEMS_OVERVIEW_HEAVY_METALS_AUDIT_VERDICT_FILTER_VALUES,
] as const

export type PackagingItemHeavyMetalsAuditFilterValue =
  (typeof PACKAGING_ITEMS_OVERVIEW_HEAVY_METALS_AUDIT_FILTER_VALUES)[number]

const HEAVY_METALS_AUDIT_STATUS_FILTER_SET = new Set<string>(
  PACKAGING_ITEMS_OVERVIEW_HEAVY_METALS_AUDIT_STATUS_FILTER_VALUES
)

export function isPackagingItemHeavyMetalsAuditStatusFilterValue(
  value: string
): value is PackagingItemHeavyMetalsAuditStatus {
  return HEAVY_METALS_AUDIT_STATUS_FILTER_SET.has(value)
}

export function normalizePackagingHeavyMetalsAuditVerdictForFilter(
  verdict: string
): string {
  if (verdict === 'TEST_REPORT_PRESENT_NOT_RELEVANT') {
    return 'TEST_REPORT_PRESENT_DEFICIENT'
  }
  return verdict
}

export function packagingItemMatchesHeavyMetalsAuditFilterValue(
  input: {
    heavyMetalsAuditStatus: PackagingItemHeavyMetalsAuditStatus
    heavyMetalsAuditLatest: { verdict: PackagingHeavyMetalsAuditVerdict } | null
  },
  filterValues: readonly string[]
): boolean {
  if (filterValues.length === 0) {
    return true
  }
  return filterValues.some((value) => {
    if (isPackagingItemHeavyMetalsAuditStatusFilterValue(value)) {
      return input.heavyMetalsAuditStatus === value
    }
    if (input.heavyMetalsAuditLatest == null) {
      return false
    }
    return (
      normalizePackagingHeavyMetalsAuditVerdictForFilter(
        input.heavyMetalsAuditLatest.verdict
      ) === normalizePackagingHeavyMetalsAuditVerdictForFilter(value)
    )
  })
}

export function packagingItemHeavyMetalsAuditStatusLabelKey(
  status: PackagingItemHeavyMetalsAuditStatus
): `list.heavyMetalsAudit.status.${PackagingItemHeavyMetalsAuditStatus}` {
  return `list.heavyMetalsAudit.status.${status}`
}

export function packagingHeavyMetalsAuditVerdictLabelKey(
  verdict: PackagingHeavyMetalsAuditVerdict
): `itemDetail.heavyMetalsAudit.verdicts.${PackagingHeavyMetalsAuditVerdict}` {
  return `itemDetail.heavyMetalsAudit.verdicts.${verdict}`
}

const HEAVY_METALS_AUDIT_VERDICT_ROLLUP_ORDER: Record<
  PackagingItemHeavyMetalsAuditStatus,
  number
> = {
  passed: 0,
  failed: 1,
  flagged: 2,
  not_audited: 3,
}

export function compareHeavyMetalsAuditVerdictFilterValues(
  left: PackagingHeavyMetalsAuditVerdict,
  right: PackagingHeavyMetalsAuditVerdict
): number {
  const leftOrder = HEAVY_METALS_AUDIT_VERDICT_ROLLUP_ORDER[
    heavyMetalsAuditVerdictToOverviewStatus(left)
  ]
  const rightOrder = HEAVY_METALS_AUDIT_VERDICT_ROLLUP_ORDER[
    heavyMetalsAuditVerdictToOverviewStatus(right)
  ]
  if (leftOrder !== rightOrder) {
    return leftOrder - rightOrder
  }
  return left.localeCompare(right)
}
